import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { FileServerService } from '@app/core/services/file-server.service';
import type {
    FileConversionStatus,
    OfficeDocumentViewType,
    WopiConvertTarget,
} from '@app/core/services/file-server.types';
import { ModalService } from '@app/core/services/modal.service';
import { exhaustMap, filter, from, Subscription, take, timer } from 'rxjs';

@Component({
    selector: 'tt-office-document-viewer',
    templateUrl: './office-document-viewer.component.html',
    styleUrls: ['./office-document-viewer.component.css'],
    standalone: false,
})
export class OfficeDocumentViewerComponent implements OnChanges, OnDestroy {
    @Input()
    public documentId = '';

    @Input()
    public canConvert = true;

    @Output()
    public conversionCompleted = new EventEmitter<FileConversionStatus>();

    @Output()
    public formatChanged = new EventEmitter<string>();

    public frameUrl?: SafeResourceUrl;

    public editing = false;

    private currentDocumentId = '';

    private loadId = 0;

    private conversionPoll?: Subscription;

    constructor(
        private fileServer: FileServerService,
        private appSettings: AppSettingsService,
        private modal: ModalService,
        private sanitizer: DomSanitizer,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes['documentId']) return;

        this.currentDocumentId = this.documentId?.trim() ?? '';
        this.loadId++;
        this.conversionPoll?.unsubscribe();
        this.frameUrl = undefined;

        if (this.currentDocumentId) void this.showDocument(this.currentDocumentId, 'view');
    }

    public ngOnDestroy(): void {
        this.loadId++;
        this.conversionPoll?.unsubscribe();
    }

    @HostListener('window:message', ['$event'])
    public onWindowMessage(event: MessageEvent): void {
        const message = this.parseMessage(event.data);
        if (message?.MessageId === 'UI_Edit') void this.editDocument();
    }

    private parseMessage(data: unknown): OfficeMessage | undefined {
        if (typeof data === 'object' && data !== null) return data as OfficeMessage;
        if (typeof data !== 'string') return undefined;

        try {
            return JSON.parse(data) as OfficeMessage;
        } catch {
            return undefined;
        }
    }

    private async editDocument(): Promise<void> {
        if (this.editing || !this.currentDocumentId) return;

        this.editing = true;

        try {
            const fileInfo = await this.fileServer.getWopiFileInfo(this.currentDocumentId);

            if (fileInfo.HostEditUrl?.trim()) {
                await this.showDocument(this.currentDocumentId, 'edit');
                return;
            }

            if (fileInfo.CanConvert !== true) {
                await this.showCannotEdit('Unfortunately you cannot edit files of this filetype.');
                return;
            }

            if (!this.canConvert) {
                await this.showCannotEdit('Unfortunately you cannot edit this file. It needs to be converted to a compatible format and conversion is not allowed from this page.');
                return;
            }

            const confirmed = await this.modal.openConfirmDialog({
                type: 'warning',
                title: 'File Conversion',
                message: "To edit this file in ONLYOFFICE we need to convert it to the newest file format. Don't worry, we will create a backup of the original file.",
                ok: 'Convert',
                cancel: 'Cancel',
            });
            if (!confirmed) return;

            const target = await this.fileServer.checkIfConvertTargetExists(this.currentDocumentId);
            await this.openConversionTarget(target);
        } catch {
            await this.showCannotEdit('Could not retrieve file information.');
        } finally {
            this.editing = false;
        }
    }

    private async openConversionTarget(target: WopiConvertTarget): Promise<void> {
        if (!target.exists) {
            await this.showDocument(this.currentDocumentId, 'convert');
            this.formatChanged.emit(target.extension);
            return;
        }

        const choice = await this.chooseExistingTarget();

        if (choice === 'existing') {
            await this.showDocument(target.documentId, 'edit');
            this.formatChanged.emit(target.extension);
        } else if (choice === 'convert') {
            await this.showDocument(this.currentDocumentId, 'convert');
        }
    }

    private async chooseExistingTarget(): Promise<'existing' | 'convert' | 'cancel'> {
        let choice: 'existing' | 'convert' | 'cancel' = 'cancel';

        await this.modal.show({
            type: 'warning',
            title: 'File Conversion',
            message: 'A file with the same filename as this file but with the newest file format already exists. Do you want to open this file instead of converting the current file?',
            buttons: [
                {
                    label: 'Open Existing',
                    cssClass: '',
                    action: (dialog) => {
                        choice = 'existing';
                        dialog.close();
                    },
                },
                {
                    label: 'Convert Current',
                    cssClass: '',
                    action: (dialog) => {
                        choice = 'convert';
                        dialog.close();
                    },
                },
                {
                    label: 'Cancel',
                    cssClass: '',
                    action: (dialog) => dialog.close(),
                },
            ],
        });

        return choice;
    }

    private async showCannotEdit(message: string): Promise<void> {
        await this.modal.show({
            type: 'secondary',
            title: 'Cannot edit file',
            message,
            buttons: [{ label: 'Ok', close: true, cssClass: '' }],
        });
    }

    private async showDocument(documentId: string, viewType: OfficeDocumentViewType): Promise<void> {
        const loadId = ++this.loadId;
        await this.appSettings.ensureIsReady();
        if (loadId !== this.loadId) return;
        const token = await this.fileServer.getWopiToken(documentId);
        if (loadId !== this.loadId) return;

        const url = this.fileServer.getOfficeDocumentUrl(documentId, viewType, token.accessToken, this.appSettings.clientSessionId);
        this.currentDocumentId = documentId;
        this.frameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

        if (viewType === 'convert') this.watchConversion(documentId);
    }

    private watchConversion(fileItemId: string): void {
        this.conversionPoll?.unsubscribe();
        let fileItemKeyno = '0';

        this.conversionPoll = timer(0, 200)
            .pipe(
                exhaustMap(() => from(this.fileServer.isConvertCompleted({ fileItemId, fileItemKeyno }))),
                filter((result) => {
                    fileItemKeyno = result.fileItemKeyno;
                    return result.isCompleted;
                }),
                take(1),
            )
            .subscribe({
                next: (result) => this.conversionCompleted.emit(result),
                error: (error) => console.error(error),
            });
    }
}

interface OfficeMessage {
    MessageId?: string;
    Values?: unknown;
}
